/**
 * @fileoverview Organism: full detail, edit and delete for one habit.
 * @module components/organisms/HabitDetailsModal
 */

import { useState } from "react"

import { Button, Icon, ProgressBar } from "@/components/atoms"
import { ConfirmDialog, Modal } from "@/components/molecules"
import {
	computeMilestoneProgress,
	formatMilestone,
} from "@/lib/domain/milestones"
import { describeSchedule } from "@/lib/domain/schedule"
import { computeHabitStats, formatRatio } from "@/lib/domain/stats"
import type { Habit, HabitEntry, HabitId, HabitPatch } from "@/types/types"

import { HabitForm } from "../HabitForm/HabitForm"

import "./HabitDetailsModal.css"

/** Props accepted by {@link HabitDetailsModal}. */
export interface HabitDetailsModalProps {
	/** The habit to show, edit or delete. */
	readonly habit: Habit
	/** Every logged entry, used to compute this habit's streak. */
	readonly entries: readonly HabitEntry[]
	/** Called when the dialog is dismissed. */
	readonly onClose: () => void
	/** Called with the changed fields once the edit form is submitted. */
	readonly onSave: (id: HabitId, patch: HabitPatch) => void
	/** Called once the deletion is confirmed. */
	readonly onDelete: (id: HabitId) => void
}

/**
 * Detail dialog for a single habit: streak and milestone progress, plus an
 * inline edit form and a confirmed delete.
 *
 * @param props - The habit, its entries and the actions available on it.
 * @returns The rendered dialog.
 */
export function HabitDetailsModal({
	habit,
	entries,
	onClose,
	onSave,
	onDelete,
}: HabitDetailsModalProps) {
	const [isEditing, setIsEditing] = useState(false)
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

	if (isEditing) {
		return (
			<Modal title={`Modifier ${habit.name}`} onClose={onClose}>
				<HabitForm
					submitLabel="Enregistrer"
					initialValue={{
						name: habit.name,
						schedule: habit.schedule,
						goalReps: habit.goalReps,
						...(habit.category === undefined
							? {}
							: { category: habit.category }),
					}}
					onSubmit={draft => {
						onSave(habit.id, draft)
						setIsEditing(false)
					}}
				/>
			</Modal>
		)
	}

	const stats = computeHabitStats(habit, entries)
	const milestones = computeMilestoneProgress(habit.totalReps)

	return (
		<Modal title={habit.name} onClose={onClose}>
			<div className="habit-details">
				{habit.category !== undefined && (
					<span className="habit-details__category">
						{habit.category}
					</span>
				)}

				<p className="habit-details__schedule">
					{describeSchedule(habit.schedule)}
				</p>

				<div className="habit-details__stats">
					<div className="habit-details__stat">
						<strong>{stats.currentStreak}</strong>
						<span>série en cours</span>
					</div>
					<div className="habit-details__stat">
						<strong>{stats.longestStreak}</strong>
						<span>meilleure série</span>
					</div>
					<div className="habit-details__stat">
						<strong>{habit.totalReps}</strong>
						<span>répétitions</span>
					</div>
				</div>

				<section aria-label="Paliers">
					<h3 className="habit-details__heading">Paliers</h3>
					<ProgressBar
						ratio={milestones.ratioToNext}
						label={`Progression vers le prochain palier de ${habit.name}`}
					/>
					<p className="habit-details__milestone-hint">
						{milestones.next === null
							? "Tous les paliers sont atteints."
							: `${formatRatio(milestones.ratioToNext)} vers ${formatMilestone(milestones.next)} répétitions`}
					</p>
					{milestones.achieved.length > 0 && (
						<ul className="habit-details__milestone-list">
							{milestones.achieved.map(tier => (
								<li
									key={tier}
									className="habit-details__milestone"
								>
									<Icon name="check" size={14} />
									{formatMilestone(tier)}
								</li>
							))}
						</ul>
					)}
				</section>

				<div className="habit-details__actions">
					<Button
						variant="secondary"
						onClick={() => {
							setIsEditing(true)
						}}
					>
						<Icon name="pencil" size={16} />
						Modifier
					</Button>
					<Button
						variant="ghost"
						danger
						onClick={() => {
							setIsConfirmingDelete(true)
						}}
					>
						<Icon name="trash" size={16} />
						Supprimer
					</Button>
				</div>
			</div>

			{isConfirmingDelete && (
				<ConfirmDialog
					title={`Supprimer ${habit.name} ?`}
					message="Cette action supprime aussi son historique de répétitions. Elle est définitive."
					danger
					confirmLabel="Supprimer"
					onConfirm={() => {
						onDelete(habit.id)
					}}
					onCancel={() => {
						setIsConfirmingDelete(false)
					}}
				/>
			)}
		</Modal>
	)
}
